import api from './api';

export interface ArtifactType {
  id: string;
  name: string;
  description?: string;
  /** IntentML / DB canonical pt-* slug when API returns migration 173+. */
  artifact_type_slug?: string | null;
  created_at: string;
  updated_at?: string;
  is_deleted: boolean;
}

class ArtifactTypeService {
  private static cache: ArtifactType[] | null = null;
  private static cacheTimestamp: number | null = null;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private static pendingRequest: Promise<ArtifactType[]> | null = null;

  static async getArtifactTypes(forceRefresh = false): Promise<ArtifactType[]> {
    // Check cache first
    if (!forceRefresh && this.cache && this.cacheTimestamp) {
      const now = Date.now();
      if (now - this.cacheTimestamp < this.CACHE_DURATION) {
        return this.cache;
      }
    }

    // If there's already a pending request, wait for it instead of making a new one
    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    // Create a new request
    this.pendingRequest = this._fetchArtifactTypes();
    
    try {
      const result = await this.pendingRequest;
      return result;
    } finally {
      this.pendingRequest = null;
    }
  }

  private static async _fetchArtifactTypes(): Promise<ArtifactType[]> {
    try {
      console.log('🔄 [ArtifactTypeService] Fetching artifact types...');

      const limit = 500;
      let offset = 0;
      let allTypes: ArtifactType[] = [];

      while (true) {
        const response = await api.get('/artifact-types', {
          params: { limit, offset },
        });
        const page: ArtifactType[] = Array.isArray(response.data) ? response.data : [];
        allTypes = [...allTypes, ...page];
        if (page.length < limit) {
          break;
        }
        offset += limit;
      }

      this.cache = allTypes;
      this.cacheTimestamp = Date.now();

      console.log('✅ [ArtifactTypeService] Fetched artifact types:', allTypes.length);
      return allTypes;
    } catch (error) {
      console.error('❌ [ArtifactTypeService] Failed to fetch artifact types:', error);
      
      // Return cached data if available, otherwise empty array
      return this.cache || [];
    }
  }

  static async getArtifactTypeById(id: string): Promise<ArtifactType | null> {
    try {
      const response = await api.get(`/artifact-types/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactTypeService] Failed to fetch artifact type:', error);
      return null;
    }
  }

  static clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = null;
  }

  // Get artifact type name by ID (uses cache)
  static async getArtifactTypeName(id: string): Promise<string> {
    const types = await this.getArtifactTypes();
    const type = types.find(t => t.id === id);
    return type?.name || 'Unknown Type';
  }

  // Get artifact type options for dropdowns
  static async getArtifactTypeOptions(): Promise<{ value: string; label: string }[]> {
    const types = await this.getArtifactTypes();
    return types.map(type => ({
      value: type.id,
      label: type.name
    }));
  }

  // Legacy method compatibility
  static async getAll(): Promise<ArtifactType[]> {
    return this.getArtifactTypes();
  }

  // Create, update, delete methods for CRUD operations
  static async create(data: { name: string; description?: string }): Promise<ArtifactType> {
    const response = await api.post('/artifact-types', data);
    // Clear cache after creation
    this.clearCache();
    return response.data;
  }

  static async update(id: string, data: { name?: string; description?: string }): Promise<ArtifactType> {
    const response = await api.put(`/artifact-types/${id}`, data);
    // Clear cache after update
    this.clearCache();
    return response.data;
  }

  static async delete(id: string): Promise<void> {
    await api.delete(`/artifact-types/${id}`);
    // Clear cache after deletion
    this.clearCache();
  }
}

export const artifactTypeService = new ArtifactTypeService();
export { ArtifactTypeService };